import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import StudentModals from "../studentModals";
import StudentSidebar from "./studentSidebar";
import StudentBreadcrumb from "./studentBreadcrumb";
import { useGetStudentByIdQuery } from "../api/studentApi";
import { useMarks } from "../../../assessment/marks/hooks/useMarks";
import { useGetAllCoursesQuery } from "../../../configuration/course/api/courseApi";

const StudentResult = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const {
    data: studentResponse,
    isLoading: studentLoading,
    error,
  } = useGetStudentByIdQuery({ id: id || "" });

  const { getMarksState } = useMarks({ student_id: id });
  const { marks, isLoading: marksLoading } = getMarksState();

  // Pass required params to useGetAllCoursesQuery
  const { data: coursesData } = useGetAllCoursesQuery({
    size: 100, // Large enough to get all courses
    page: 1,
  });

  // Get course info from student data
  const courseInfo = studentResponse?.data?.student?.course;

  // Find the full course details including stages
  const fullCourseInfo = coursesData?.data?.items?.find(
    (course) => course.id === courseInfo?.id
  );

  // Use course stages from the API
  const stagesWithMarks =
    fullCourseInfo?.courseStages?.map((stage) => {
      const mark = marks?.find((m) => m.course?.stage?.name === stage.name);
      return {
        ...stage,
        obtained: mark?.course?.stage?.obtained || "-",
        max: stage.max_marks,
        min: stage.min_marks,
      };
    }) || [];

  // Determine if the student passed the stage
  const getResult = (obtained: string | number, minMarks: number) => {
    if (obtained === "-") return "-";
    return Number(obtained) >= minMarks ? (
      <span className="badge badge-soft-success d-inline-flex align-items-center">
        <i className="ti ti-circle-filled fs-5 me-1"></i>
        Pass
      </span>
    ) : (
      <span className="badge badge-soft-danger d-inline-flex align-items-center">
        <i className="ti ti-circle-filled fs-5 me-1"></i>
        Fail
      </span>
    );
  };

  if (studentLoading || marksLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <StudentBreadcrumb studentId={id} />
          </div>
          <div className="row">
            <StudentSidebar />
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>

                  <div className="card">
                    <div className="card-body p-0 py-3">
                      <div className="text-center">
                        Loading student results...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>Error loading student data</div>;
  }

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            {/* Page Header */}
            <StudentBreadcrumb studentId={id} />
            {/* /Page Header */}
          </div>
          <div className="row">
            {/* Student Information */}
            <StudentSidebar />
            {/* /Student Information */}
            <div className="col-xxl-9 col-xl-8">
              <div className="row">
                <div className="col-md-12">
                  {/* List */}
                  <ul className="nav nav-tabs nav-tabs-bottom mb-4">
                    <li>
                      <Link
                        to={`${routes.studentDetail.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-school me-2" />
                        Student Details
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentAttendanceDetail.replace(
                          ":id",
                          id || ""
                        )}`}
                        className="nav-link"
                      >
                        <i className="ti ti-calendar-due me-2" />
                        Attendance
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentFees.replace(":id", id || "")}`}
                        className="nav-link"
                      >
                        <i className="ti ti-report-money me-2" />
                        Fees
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={`${routes.studentResult.replace(":id", id || "")}`}
                        className="nav-link active"
                      >
                        <i className="ti ti-bookmark-edit me-2" />
                        Results
                      </Link>
                    </li>
                  </ul>
                  {/* /List */}
                  <div className="card">
                    <div className="card-header">
                      <h4 className="mb-3">
                        Level Results -{" "}
                        {courseInfo?.name || "No Course Assigned"}
                      </h4>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table">
                          <thead className="thead-light">
                            <tr>
                              <th>Level</th>
                              <th>Max Marks</th>
                              <th>Min Marks</th>
                              <th>Marks Obtained</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {courseInfo ? (
                              <>
                                {stagesWithMarks.map((stage) => (
                                  <tr key={stage.id}>
                                    <td>{stage.name}</td>
                                    <td>{stage.max_marks}</td>
                                    <td>{stage.min_marks}</td>
                                    <td>{stage.obtained}</td>
                                    <td>
                                      {getResult(
                                        stage.obtained,
                                        stage.min_marks
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                {stagesWithMarks.length > 0 && (
                                  <tr>
                                    <td className="bg-dark text-white">
                                      Overall Progress: {marks?.length || 0}/
                                      {stagesWithMarks.length} Levels
                                    </td>
                                    <td className="bg-dark text-white">
                                      Total Max:{" "}
                                      {stagesWithMarks.reduce(
                                        (sum, stage) =>
                                          sum + Number(stage.max_marks),
                                        0
                                      )}
                                    </td>
                                    <td className="bg-dark text-white">
                                      Total Min:{" "}
                                      {stagesWithMarks.reduce(
                                        (sum, stage) =>
                                          sum + Number(stage.min_marks),
                                        0
                                      )}
                                    </td>
                                    <td
                                      className="bg-dark text-white"
                                      colSpan={2}
                                    >
                                      Total Obtained:{" "}
                                      {marks?.reduce(
                                        (sum, mark) =>
                                          sum +
                                          (typeof mark.obtained === "number"
                                            ? mark.obtained
                                            : 0),
                                        0
                                      ) || 0}
                                    </td>
                                  </tr>
                                )}
                              </>
                            ) : (
                              <tr>
                                <td colSpan={5} className="text-center">
                                  No course assigned
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      <StudentModals />
    </>
  );
};

export default StudentResult;
